import React from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from '../ui/button';
import { Save, RefreshCw, RotateCcw, FileText, Bookmark, ArrowLeftRight, X, Sparkles } from 'lucide-react';
import TeeTimeGroup from './TeeTimeGroup';
import InsertTeeTimeButton from './InsertTeeTimeButton';
import './TeeSheetDisplay.css';

function TeeSheetDisplay({ teeSheet, conflicts, meritData, paceData, overflowPlayers, activeId, onMoveGroupUp, onMoveGroupDown, onDeleteGroup, feedbackByPlayer, onFeedbackClick, onInsertTeeTime, tournament, unsavedChanges, onVersionBump, onReprocess, onExport, onSave, onFullReset, conflictCount, feedbackCount, requestFulfillment, swapMode, swapSelection, onSwapModeToggle, onSwapSelect, onCancelSwap, onGenerateTeeSheet, isGenerating }) {
  console.log('TeeSheetDisplay props:', { teeSheet, conflicts, meritData, paceData });
  console.log('TeeSheetDisplay teeSheet type:', typeof teeSheet);
  console.log('TeeSheetDisplay teeSheet value:', teeSheet);
  console.log('TeeSheetDisplay teeSheet.groups exists?', teeSheet?.groups);
  console.log('TeeSheetDisplay teeSheet.groups length:', teeSheet?.groups?.length);


  const getGroupConflicts = (groupId) => {
    // Ensure conflicts is always an array
    const conflictArray = Array.isArray(conflicts) ? conflicts : [];
    return conflictArray.filter(conflict => 
      conflict.groupIndex === groupId || 
      (conflict.groups && conflict.groups.includes(groupId))
    );
  };

  const getPlayerMeritRank = (ghin) => {
    const merit = meritData.find(m => m.ghin === ghin);
    return merit?.oomRank || 'N/A';
  };

  const getPlayerPaceRank = (ghin) => {
    const pace = paceData.find(p => p.ghin === ghin);
    console.log('TeeSheetDisplay getPlayerPaceRank called:', {
      ghin,
      paceDataLength: paceData?.length,
      foundPace: !!pace,
      rank: pace?.rank,
      returning: pace?.rank || 'N/A'
    });
    return pace?.rank || 'N/A';
  };

  if (!teeSheet || !teeSheet.groups) {
    return (
      <div className="tee-sheet-display empty">
        <div className="empty-state">
          <h3>No Tee Sheet Data</h3>
          <p>No players have been imported for this tournament yet.</p>
          <p>Please import player data first to generate tee times.</p>
        </div>
      </div>
    );
  }

  if (teeSheet.groups.length === 0) {
    return (
      <div className="tee-sheet-display empty">
        <div className="empty-state">
          <h3>No Tee Sheet Generated</h3>
          <p>Click the button below to generate tee times using the extracted rules.</p>
          {onGenerateTeeSheet && (
            <Button
              onClick={onGenerateTeeSheet}
              variant="default"
              size="lg"
              className="generate-btn"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate Tee Sheet
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tee-sheet-display">
      <div className="tee-sheet-header">
        <div className="tee-sheet-header-left">
          <h2>Tee Sheet <span className="tee-sheet-version">v{tournament?.version || 1}</span></h2>
        </div>
        <div className="tee-sheet-header-right">
          <div className="header-controls">
            {onSave && unsavedChanges && (
              <Button
                onClick={onSave}
                variant="default"
                size="sm"
                className="header-btn save-btn"
                title="Save changes"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            )}
            {onReprocess && (
              <Button
                onClick={() => onReprocess(true)}
                variant="outline"
                size="sm"
                className="header-btn"
                title="Reprocess tee sheet"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Reprocess
              </Button>
            )}
            {onFullReset && (
              <Button
                onClick={onFullReset}
                variant="outline"
                size="sm"
                className="header-btn"
                title="Full reset - clear all manual changes and reprocess"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Full Reset
              </Button>
            )}
            {onExport && (
              <Button
                onClick={() => onExport('pdf')}
                variant="outline"
                size="sm"
                className="header-btn"
                title="Export as PDF"
              >
                <FileText className="h-4 w-4 mr-1" />
                Export PDF
              </Button>
            )}
            {onVersionBump && (
              <Button
                onClick={onVersionBump}
                variant="secondary"
                size="sm"
                className="header-btn"
                disabled={!teeSheet || !teeSheet.groups || teeSheet.groups.length === 0 || unsavedChanges}
                title={unsavedChanges ? "Please save the tee sheet first" : "Create snapshot and bump version"}
              >
                <Bookmark className="h-4 w-4 mr-1" />
                Bump Version
              </Button>
            )}

            {/* Swap Mode Controls */}
            <div className="swap-controls">
              <span className="swap-label">Swap:</span>
              <div className="swap-buttons">
                <Button
                  onClick={() => onSwapModeToggle && onSwapModeToggle('one')}
                  variant={swapMode === 'one' ? 'default' : 'outline'}
                  size="sm"
                  className={`swap-btn ${swapMode === 'one' ? 'active' : ''}`}
                  title="Swap single player"
                >
                  One
                </Button>
                <Button
                  onClick={() => onSwapModeToggle && onSwapModeToggle('two')}
                  variant={swapMode === 'two' ? 'default' : 'outline'}
                  size="sm"
                  className={`swap-btn ${swapMode === 'two' ? 'active' : ''}`}
                  title="Swap pair (player + next)"
                >
                  Two
                </Button>
                <Button
                  onClick={() => onSwapModeToggle && onSwapModeToggle('group')}
                  variant={swapMode === 'group' ? 'default' : 'outline'}
                  size="sm"
                  className={`swap-btn ${swapMode === 'group' ? 'active' : ''}`}
                  title="Swap entire group"
                >
                  Group
                </Button>
                {swapMode && (
                  <Button
                    onClick={() => {
                      onCancelSwap && onCancelSwap();
                      onSwapModeToggle && onSwapModeToggle(swapMode);
                    }}
                    variant="ghost"
                    size="sm"
                    className="swap-cancel-btn"
                    title="Exit swap mode"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Swap Mode Indicator */}
      {swapMode && (
        <div className="swap-mode-indicator">
          <ArrowLeftRight className="h-4 w-4" />
          <span>
            Swap Mode: <strong>{swapMode === 'one' ? 'Single Player' : swapMode === 'two' ? 'Pair' : 'Entire Group'}</strong>
            {swapSelection ? ' - Click another to swap' : ' - Click a player/group to select'}
          </span>
          {swapSelection && (
            <Button
              onClick={onCancelSwap}
              variant="ghost"
              size="sm"
              className="cancel-selection-btn"
            >
              Cancel Selection
            </Button>
          )}
        </div>
      )}

      <div className="tee-sheet-table-container">
        <table className="tee-sheet-table">
          <tbody>
            {teeSheet.groups.map((group, index) => (
              <React.Fragment key={`group-${group.id}`}>
                {/* Insert button above each group */}
                <tr className="insert-tee-time-row">
                  <td className="insert-button-cell">
                    <InsertTeeTimeButton 
                      position={index} 
                      onInsert={onInsertTeeTime}
                      className="top"
                    />
                  </td>
                  <td colSpan="4"></td>
                </tr>
                
                {/* Actual tee time group */}
                <TeeTimeGroup
                  group={group}
                  conflicts={getGroupConflicts(group.id)}
                  getPlayerMeritRank={getPlayerMeritRank}
                  getPlayerPaceRank={getPlayerPaceRank}
                  isActive={activeId !== null}
                  onMoveGroupUp={onMoveGroupUp}
                  onMoveGroupDown={onMoveGroupDown}
                  onDeleteGroup={onDeleteGroup}
                  isFirst={index === 0}
                  isLast={index === teeSheet.groups.length - 1}
                  totalGroups={teeSheet.groups.length}
                  feedbackByPlayer={feedbackByPlayer}
                  onFeedbackClick={onFeedbackClick}
                  requestFulfillment={requestFulfillment}
                  swapMode={swapMode}
                  swapSelection={swapSelection}
                  onSwapSelect={onSwapSelect}
                />
                
                {/* Insert button after the last group */}
                {index === teeSheet.groups.length - 1 && (
                  <tr className="insert-tee-time-row">
                    <td className="insert-button-cell">
                      <InsertTeeTimeButton 
                        position={index + 1} 
                        onInsert={onInsertTeeTime}
                        className="bottom"
                      />
                    </td>
                    <td colSpan="4"></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeeSheetDisplay;